/**
 * Caminho do token arrastado (`docs/spec/table-media.md`, "Caminho do movimento"), como o
 * `rrpgScene_MovementPath.dlua` do original com o `TSC3GridLayer.CalculateShortestPath` nativo:
 * - o trecho atual é o caminho mais curto na grade, da célula onde ele começa até a célula onde o
 *   centro do token está (A*, 8 vizinhos, a diagonal conforme a regra da grade);
 * - parado 0,7 s numa célula, o trecho fecha ali quando o token volta a andar: o próximo começa
 *   nessa célula, e a distância soma. Parado quer dizer o token e o mouse: preso numa parede com o
 *   mouse andando, o token não está parando;
 * - com as paredes valendo, o caminho as contorna; sem saída, vai por cima delas.
 * Funções puras, em células.
 */
import { moveWithWalls, type SightSeg } from './vision.js';

/** Célula da grade: `i` é a coluna, `j` a linha. */
export type Cell = { i: number; j: number };
/** Como a diagonal conta (`TSC3SquareDiagonals`): 1, 2, 1 e 2 alternados, ou √2 células. */
export type Diagonals = 'simplified' | 'manhattan' | 'alternated' | 'euclidean';

export type PathOptions = {
  cols: number;
  rows: number;
  diagonals?: Diagonals;
  /** diagonais já feitas antes (a regra alternada continua de um trecho para o outro) */
  diagonalsSoFar?: number;
  /** o passo de uma célula para a vizinha esbarra em algo */
  blocked?: (from: Cell, to: Cell) => boolean;
};

export type Path = {
  /** do começo ao fim, com as duas pontas */
  steps: Cell[];
  /** em células, com a regra das diagonais */
  cost: number;
  /** diagonais feitas até o fim, contando as de antes */
  diagonals: number;
};

/** Quanto o caminho pode sair da grade (o `endOfGrid = "continue"` do original). */
const MARGIN = 10;
/** Células abertas antes de desistir (destino fechado por paredes). */
const MAX_EXPANDED = 20_000;
/** Custos em milésimos de célula, inteiros como no original (empates exatos). */
const STEP = 1000;
/** Os vizinhos, na ordem do original: os quatro lados, depois as quatro diagonais. */
const NEIGHBORS: readonly (readonly [number, number])[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

const SQRT2_STEP = Math.round(Math.SQRT2 * STEP);

function diagonalCost(rule: Diagonals, diagonalsSoFar: number): number {
  if (rule === 'manhattan') return 2 * STEP;
  if (rule === 'alternated') return diagonalsSoFar % 2 === 0 ? STEP : 2 * STEP;
  if (rule === 'euclidean') return SQRT2_STEP;
  return STEP;
}

/**
 * Quanto falta, no mínimo, sem paredes. O original usa a contagem de passos em todas as regras; nas
 * que a diagonal custa mais que 1, a busca se espalhava demais em caminhos longos. Aqui cada regra
 * tem a conta do campo aberto (exata; na alternada, às vezes 1 a menos): o custo do caminho achado
 * é o mesmo.
 */
function estimate(rule: Diagonals, di: number, dj: number): number {
  const hi = Math.max(di, dj);
  const lo = Math.min(di, dj);
  if (rule === 'manhattan') return (hi + lo) * STEP;
  if (rule === 'alternated') return (hi + Math.floor(lo / 2)) * STEP;
  if (rule === 'euclidean') return (hi - lo) * STEP + lo * SQRT2_STEP;
  return hi * STEP;
}

type Node = { i: number; j: number; g: number; perp: number; diag: number; parent: Node | null; closed: boolean; stamp: number };
type Entry = { node: Node; stamp: number; f: number; perp: number; d2: number };

/**
 * A ordem da fila do original (`TSC3ComparerAbertoItemAEstrela`): menor custo estimado; no empate,
 * o caminho que menos se afasta da reta entre as pontas (a soma das distâncias das células a ela),
 * depois a célula mais perto do destino. É o que faz o caminho seguir reto em vez de "primeiro a
 * diagonal, depois o resto".
 */
const entryOrder = (a: Entry, b: Entry): number => a.f - b.f || a.perp - b.perp || a.d2 - b.d2 || a.node.i - b.node.i || a.node.j - b.node.j;

/** Caminho mais curto de uma célula a outra; null se forem a mesma ou se não houver caminho. */
export function shortestPath(from: Cell, to: Cell, o: PathOptions): Path | null {
  if (from.i === to.i && from.j === to.j) return null;
  const rule = o.diagonals ?? 'simplified';
  const inside = (i: number, j: number) => i >= -MARGIN && i <= o.cols + MARGIN && j >= -MARGIN && j <= o.rows + MARGIN;
  if (!inside(from.i, from.j) || !inside(to.i, to.j)) return null;
  const lx = to.i - from.i;
  const ly = to.j - from.j;
  const len = Math.hypot(lx, ly);
  const perpOf = (i: number, j: number) => Math.round((Math.abs(ly * (i - from.i) - lx * (j - from.j)) / len) * STEP);
  const h = (i: number, j: number) => estimate(rule, Math.abs(to.i - i), Math.abs(to.j - j));
  const d2 = (i: number, j: number) => Math.round(((to.i - i) ** 2 + (to.j - j) ** 2) * STEP);
  const span = o.rows + 2 * MARGIN + 1;
  const key = (i: number, j: number) => (i + MARGIN) * span + (j + MARGIN);
  const nodes = new Map<number, Node>();
  const open = new Heap<Entry>(entryOrder);
  const push = (n: Node) => {
    n.stamp++;
    open.push({ node: n, stamp: n.stamp, f: n.g + h(n.i, n.j), perp: n.perp, d2: d2(n.i, n.j) });
  };
  const start: Node = { i: from.i, j: from.j, g: 0, perp: 0, diag: o.diagonalsSoFar ?? 0, parent: null, closed: false, stamp: 0 };
  nodes.set(key(start.i, start.j), start);
  push(start);
  let expanded = 0;
  for (let e = open.pop(); e; e = open.pop()) {
    const n = e.node;
    // entrada velha: a célula voltou à fila com um caminho melhor
    if (e.stamp !== n.stamp || n.closed) continue;
    if (n.i === to.i && n.j === to.j) {
      const steps: Cell[] = [];
      for (let c: Node | null = n; c; c = c.parent) steps.push({ i: c.i, j: c.j });
      return { steps: steps.reverse(), cost: n.g / STEP, diagonals: n.diag };
    }
    n.closed = true;
    if (++expanded > MAX_EXPANDED) return null;
    for (const [di, dj] of NEIGHBORS) {
      const i = n.i + di;
      const j = n.j + dj;
      if (!inside(i, j) || o.blocked?.(n, { i, j })) continue;
      const diagonal = di !== 0 && dj !== 0;
      const g = n.g + (diagonal ? diagonalCost(rule, n.diag) : STEP);
      const perp = n.perp + perpOf(i, j);
      const diag = n.diag + (diagonal ? 1 : 0);
      const old = nodes.get(key(i, j));
      if (!old) {
        const m: Node = { i, j, g, perp, diag, parent: n, closed: false, stamp: 0 };
        nodes.set(key(i, j), m);
        push(m);
        continue;
      }
      // como no original: só troca por um caminho mais barato ou, no empate, mais perto da reta
      if (!(g < old.g || (g === old.g && perp < old.perp))) continue;
      Object.assign(old, { g, perp, diag, parent: n, closed: false });
      push(old);
    }
  }
  return null;
}

/** Fila de prioridade (heap binário). */
class Heap<T> {
  private items: T[] = [];
  constructor(private readonly order: (a: T, b: T) => number) {}
  push(item: T): void {
    const a = this.items;
    a.push(item);
    for (let k = a.length - 1; k > 0; ) {
      const p = (k - 1) >> 1;
      if (this.order(a[k]!, a[p]!) >= 0) break;
      [a[k], a[p]] = [a[p]!, a[k]!];
      k = p;
    }
  }
  pop(): T | undefined {
    const a = this.items;
    const top = a[0];
    const last = a.pop();
    if (!a.length || last === undefined) return top;
    a[0] = last;
    for (let k = 0; ; ) {
      const l = 2 * k + 1;
      const r = l + 1;
      let m = k;
      if (l < a.length && this.order(a[l]!, a[m]!) < 0) m = l;
      if (r < a.length && this.order(a[r]!, a[m]!) < 0) m = r;
      if (m === k) break;
      [a[k], a[m]] = [a[m]!, a[k]!];
      k = m;
    }
    return top;
  }
}

/* ------------------------------------------------------------------ paredes */

/** As paredes por célula, para o caminho conferir só as de perto. */
export type WallIndex = { near: (minX: number, minY: number, maxX: number, maxY: number) => SightSeg[] };

/** Parede que cobre mais células que isso fica numa lista à parte, conferida sempre. */
const WIDE_WALL_CELLS = 1024;
const cellKey = (i: number, j: number) => (i + 32768) * 65536 + (j + 32768);

export function wallIndex(segs: SightSeg[]): WallIndex {
  const cells = new Map<number, SightSeg[]>();
  const wide: SightSeg[] = [];
  for (const s of segs) {
    const i0 = Math.floor(Math.min(s.ax, s.bx));
    const i1 = Math.floor(Math.max(s.ax, s.bx));
    const j0 = Math.floor(Math.min(s.ay, s.by));
    const j1 = Math.floor(Math.max(s.ay, s.by));
    if ((i1 - i0 + 1) * (j1 - j0 + 1) > WIDE_WALL_CELLS) {
      wide.push(s);
      continue;
    }
    for (let i = i0; i <= i1; i++)
      for (let j = j0; j <= j1; j++) {
        const list = cells.get(cellKey(i, j));
        if (list) list.push(s);
        else cells.set(cellKey(i, j), [s]);
      }
  }
  return {
    near(minX, minY, maxX, maxY) {
      const out = new Set<SightSeg>(wide);
      for (let i = Math.floor(minX); i <= Math.floor(maxX); i++)
        for (let j = Math.floor(minY); j <= Math.floor(maxY); j++) for (const s of cells.get(cellKey(i, j)) ?? []) out.add(s);
      return [...out];
    },
  };
}

/** Raio do círculo que o caminho leva para conferir as paredes (o original usa 20% de meio lado). */
export const pathRadius = (size: number): number => size * 0.1;

/**
 * O passo de uma célula para a vizinha esbarra numa parede que barra o movimento? O centro do
 * token anda de centro a centro das células, deslocado por `ox`, `oy` (tokens de lado par ficam no
 * canto da célula), com um círculo de raio `radius`.
 */
export function wallBlocker(index: WallIndex, radius: number, ox: number, oy: number): (from: Cell, to: Cell) => boolean {
  return (a, b) => {
    const ax = a.i + 0.5 + ox;
    const ay = a.j + 0.5 + oy;
    const bx = b.i + 0.5 + ox;
    const by = b.j + 0.5 + oy;
    const segs = index.near(Math.min(ax, bx) - radius, Math.min(ay, by) - radius, Math.max(ax, bx) + radius, Math.max(ay, by) + radius);
    return segs.length > 0 && moveWithWalls({ x: ax, y: ay }, { x: bx, y: by }, radius, segs).blocked;
  };
}

/* ------------------------------------------------------------------ rastro */

/** Parado na mesma célula (o token e o mouse) por mais que isso, o trecho fecha quando o token volta a andar. */
export const SETTLE_MS = 700;

/** Rastro do token arrastado: os trechos fechados e o atual. */
export type MoveTrack = {
  /** do centro da célula ao centro do token, em células (o token de lado par fica no canto) */
  ox: number;
  oy: number;
  /** as células que o token cobre, em relação à célula do centro dele */
  shape: Cell[];
  /** trechos fechados: o token parou no fim de cada um */
  done: Path[];
  /** onde o trecho atual começa, e o que veio antes dele */
  from: Cell;
  before: number;
  diagonalsBefore: number;
  /** o trecho atual (null: o token está onde o trecho começa) */
  path: Path | null;
  /** a célula do centro do token, e o centro */
  at: Cell;
  x: number;
  y: number;
  /** a célula onde o mouse quer pôr o centro do token (antes das paredes) */
  pointer: Cell;
  /** quando o token ou o mouse andou de célula pela última vez (ms) */
  movedAt: number;
};

type TokenBox = { x: number; y: number; size: number };

/** As células que o token cobre, em relação à célula do centro. */
function footprint({ x, y, size }: TokenBox, center: Cell): Cell[] {
  const eps = 1e-6;
  const out: Cell[] = [];
  for (let i = Math.floor(x + eps); i <= Math.ceil(x + size - eps) - 1; i++)
    for (let j = Math.floor(y + eps); j <= Math.ceil(y + size - eps) - 1; j++) out.push({ i: i - center.i, j: j - center.j });
  return out;
}

const centerCell = (x: number, y: number): Cell => ({ i: Math.floor(x), j: Math.floor(y) });

/** Começa o rastro onde o token está (canto superior esquerdo e lado, em células). */
export function startTrack(token: TokenBox, now: number): MoveTrack {
  const x = token.x + token.size / 2;
  const y = token.y + token.size / 2;
  const at = centerCell(x, y);
  return { ox: x - (at.i + 0.5), oy: y - (at.j + 0.5), shape: footprint(token, at), done: [], from: at, before: 0, diagonalsBefore: 0, path: null, at, x, y, pointer: at, movedAt: now };
}

/**
 * O token foi para `token`, com o mouse querendo pôr o centro dele em `pointer` (a célula antes das
 * paredes). Encaixado na grade, só conta mudar de célula; solto (`free`), qualquer mudança do
 * centro. `find` acha o caminho do trecho (com ou sem paredes).
 */
export function advanceTrack(track: MoveTrack, token: TokenBox, pointer: Cell, now: number, free: boolean, find: (from: Cell, to: Cell, diagonalsSoFar: number) => Path | null): MoveTrack {
  const x = token.x + token.size / 2;
  const y = token.y + token.size / 2;
  const at = centerCell(x, y);
  const sameCell = at.i === track.at.i && at.j === track.at.j;
  if (free ? x === track.x && y === track.y : sameCell) {
    // o token não saiu do lugar (preso numa parede), mas o mouse andou: não é uma parada
    return pointer.i !== track.pointer.i || pointer.j !== track.pointer.j ? { ...track, pointer, movedAt: now } : track;
  }
  let t = track;
  // parou o bastante onde estava: o trecho fecha lá
  if (t.path && now - t.movedAt > SETTLE_MS) t = { ...t, done: [...t.done, t.path], from: t.at, before: t.before + t.path.cost, diagonalsBefore: t.path.diagonals, path: null };
  const path = sameCell && t.path !== null ? t.path : at.i === t.from.i && at.j === t.from.j ? null : find(t.from, at, t.diagonalsBefore);
  return { ...t, path, at, x, y, pointer, movedAt: now };
}

/** O que desenhar, em células: as linhas, as células usadas, a ponta com a seta e a distância. */
export type TrackDrawing = {
  /** uma linha por trecho: pares x, y */
  lines: number[][];
  cells: Cell[];
  end: { x: number; y: number };
  /** as duas pontas da seta, que saem de `end` */
  arrow: [{ x: number; y: number }, { x: number; y: number }];
  /** em células, com a regra das diagonais */
  cost: number;
};

export function drawTrack(track: MoveTrack): TrackDrawing | null {
  const paths = track.path ? [...track.done, track.path] : track.done;
  const last = paths[paths.length - 1];
  if (!last) return null;
  const px = (c: Cell) => c.i + 0.5 + track.ox;
  const py = (c: Cell) => c.j + 0.5 + track.oy;
  const seen = new Set<number>();
  const cells: Cell[] = [];
  for (const p of paths)
    for (const s of p.steps)
      for (const d of track.shape) {
        const c = { i: s.i + d.i, j: s.j + d.j };
        if (seen.has(cellKey(c.i, c.j))) continue;
        seen.add(cellKey(c.i, c.j));
        cells.push(c);
      }
  const a = last.steps[last.steps.length - 2]!;
  const b = last.steps[last.steps.length - 1]!;
  const end = { x: px(b), y: py(b) };
  // a seta do original: duas hastes de 0,35 célula para trás, abertas 0,18 para cada lado
  const len = Math.hypot(b.i - a.i, b.j - a.j);
  const ux = (b.i - a.i) / len;
  const uy = (b.j - a.j) / len;
  const back = { x: end.x - ux * 0.35, y: end.y - uy * 0.35 };
  return {
    lines: paths.map((p) => p.steps.flatMap((s) => [px(s), py(s)])),
    cells,
    end,
    arrow: [
      { x: back.x - uy * 0.18, y: back.y + ux * 0.18 },
      { x: back.x + uy * 0.18, y: back.y - ux * 0.18 },
    ],
    cost: track.before + (track.path?.cost ?? 0),
  };
}
